import type { Routes } from '@angular/router';
import { devToolsGuard } from './core/guards/dev-tools.guard';
import { mockPagesGuard, mockHomeRedirect } from './core/guards/mock-pages.guard';
import { authGuard, permissionGuard } from './core/guards/route-guards';

/**
 * Every leaf route is lazy via `loadComponent` or, for a route group, via
 * `loadChildren` importing an UPPER_SNAKE `Routes` export from that feature's
 * own `<feature>.routes.ts`.
 *
 * Four shells, each a parent route whose children are its pages: `sign-in` (auth shell), `ess`
 * (mobile-first employee space), `ops` (platform operations) and `''` (main shell, the rest).
 * Navigating between pages in one shell only swaps `<router-outlet>`'s content, so the header and
 * toast host are never re-created (asserted in `main-layout.spec.ts`). A guard on a shell route
 * means a person who may not enter never downloads the shell or its pages. The main shell is
 * declared last because its `'**'` child would otherwise take every path first.
 *
 * A wildcard declared earlier in a `Routes` array matches every
 * navigation before the router ever tries the routes after it -- Angular walks
 * the array in order and stops at the first match.
 *
 * `/not-found` and `'**'` share `NotFoundComponent`, which renders in place rather
 * than `redirectTo`-ing anywhere, so a mistyped URL stays visible in the address bar.
 *
 * Every route is guarded or deliberately public. Public: `sign-in`, `forbidden`,
 * `not-found` and the wildcard. `dev/components` is the component gallery: `devToolsGuard`
 * matches it only in development, and in production it falls through to the 404. The lazy
 * chunk is still emitted into a production build, since a route table is static; the guard
 * stops it being fetched or shown. Guards are `canMatch`, so a route the user may not
 * enter never downloads its chunk. `/ess` is the user's own space and needs only a
 * session. `/ops` is platform operations and needs the platform permission. Every page
 * added in Stage 2 names a real permission with `permissionGuard`.
 *
 * `/employees` and `/leave` render sample data and have no API behind them yet, so they carry
 * `mockPagesGuard` like every feature-area page. Without it a real deployment showed invented
 * employees and leave to a signed-in company (falaq-digital/ecom-api#1943). Home stays
 * unguarded because its facade reports `unavailable` itself when `mockApi` is off.
 */
export const routes: Routes = [
  {
    path: 'sign-in',
    loadComponent: () => import('./layout/shells/auth-shell').then((m) => m.AuthShell),
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./features/sign-in/sign-in.component').then((m) => m.SignInComponent),
        title: 'Sign in',
      },
    ],
  },
  {
    path: 'ess',
    canMatch: [authGuard],
    loadComponent: () => import('./layout/shells/ess-shell').then((m) => m.EssShell),
    children: [
      {
        path: '',
        // With sample data on, the self-service home already exists under /me.
        canActivate: [mockHomeRedirect('/me')],
        data: { name: 'My space' },
        loadComponent: () =>
          import('./features/areas/area-placeholder.component').then(
            (m) => m.AreaPlaceholderComponent,
          ),
        title: 'My space',
      },
    ],
  },
  {
    path: 'me',
    canMatch: [authGuard],
    loadComponent: () => import('./layout/shells/ess-shell').then((m) => m.EssShell),
    children: [
      {
        path: '',
        loadChildren: () =>
          import('./features/experience/experience.routes').then((m) => m.EXPERIENCE_ME_ROUTES),
      },
      {
        path: '',
        loadChildren: () =>
          import('./features/platform/platform.routes').then((m) => m.PLATFORM_ME_ROUTES),
      },
      {
        path: '',
        loadChildren: () => import('./features/pay/pay.routes').then((m) => m.PAY_ME_ROUTES),
      },
      {
        path: '',
        loadChildren: () =>
          import('./features/incentives/incentives.routes').then((m) => m.INCENTIVES_ME_ROUTES),
      },
      {
        path: '',
        loadChildren: () =>
          import('./features/lifecycle/lifecycle.routes').then((m) => m.LIFECYCLE_ME_ROUTES),
      },
    ],
  },
  {
    path: 'manage',
    canMatch: [authGuard],
    loadComponent: () => import('./layout/shells/ess-shell').then((m) => m.EssShell),
    children: [
      {
        path: '',
        loadChildren: () =>
          import('./features/experience/experience.routes').then((m) => m.EXPERIENCE_MANAGE_ROUTES),
      },
    ],
  },
  {
    path: 'clock',
    canMatch: [authGuard],
    loadComponent: () => import('./layout/shells/ess-shell').then((m) => m.EssShell),
    children: [
      {
        path: '',
        loadChildren: () =>
          import('./features/attendance/attendance.routes').then((m) => m.ATTENDANCE_CLOCK_ROUTES),
      },
    ],
  },
  {
    path: 'ops',
    canMatch: [permissionGuard('Hrm.PlatformTenants.Create')],
    loadComponent: () => import('./layout/shells/ops-shell').then((m) => m.OpsShell),
    children: [
      {
        path: '',
        // With sample data on, the company list is the operator console's main page.
        canActivate: [mockHomeRedirect('/ops/tenants')],
        data: { name: 'Operations' },
        loadComponent: () =>
          import('./features/areas/area-placeholder.component').then(
            (m) => m.AreaPlaceholderComponent,
          ),
        title: 'Operations',
      },
      {
        path: '',
        loadChildren: () => import('./features/ops/ops.routes').then((m) => m.OPS_ROUTES),
      },
    ],
  },
  {
    path: '',
    loadComponent: () =>
      import('./layout/main-layout/main-layout').then((m) => m.MainLayoutComponent),
    children: [
      {
        path: '',
        pathMatch: 'full',
        canMatch: [authGuard],
        loadComponent: () => import('./features/home/home.component').then((m) => m.HomeComponent),
        title: 'Home',
      },
      {
        path: 'employees',
        canMatch: [mockPagesGuard, permissionGuard('Hrm.Employees.View')],
        loadComponent: () =>
          import('./features/employees/employees-list.component').then(
            (m) => m.EmployeesListComponent,
          ),
        title: 'Employees',
      },
      {
        path: 'leave',
        canMatch: [mockPagesGuard, authGuard],
        loadComponent: () =>
          import('./features/leave/leave-list.component').then((m) => m.LeaveListComponent),
        title: 'Leave requests',
      },
      {
        path: '',
        loadChildren: () =>
          import('./features/platform/platform.routes').then((m) => m.PLATFORM_ROUTES),
      },
      {
        path: '',
        loadChildren: () =>
          import('./features/approvals/approvals.routes').then((m) => m.APPROVALS_ROUTES),
      },
      {
        path: '',
        loadChildren: () => import('./features/org/org.routes').then((m) => m.ORG_ROUTES),
      },
      {
        path: '',
        loadChildren: () =>
          import('./features/employees/employees.routes').then((m) => m.EMPLOYEES_ROUTES),
      },
      {
        path: '',
        loadChildren: () =>
          import('./features/attendance/attendance.routes').then((m) => m.ATTENDANCE_ROUTES),
      },
      {
        path: '',
        loadChildren: () => import('./features/leave/leave.routes').then((m) => m.LEAVE_ROUTES),
      },
      {
        path: '',
        loadChildren: () => import('./features/pay/pay.routes').then((m) => m.PAY_ROUTES),
      },
      {
        path: '',
        loadChildren: () =>
          import('./features/incentives/incentives.routes').then((m) => m.INCENTIVES_ROUTES),
      },
      {
        path: '',
        loadChildren: () =>
          import('./features/lifecycle/lifecycle.routes').then((m) => m.LIFECYCLE_ROUTES),
      },
      {
        path: '',
        loadChildren: () =>
          import('./features/experience/experience.routes').then((m) => m.EXPERIENCE_ROUTES),
      },
      {
        path: '',
        loadChildren: () =>
          import('./features/reports/reports.routes').then((m) => m.REPORTS_ROUTES),
      },
      {
        path: 'dev/components',
        canMatch: [devToolsGuard],
        data: { layout: { hideHeader: true, hideBottomBar: true } },
        loadComponent: () =>
          import('./features/dev-components/dev-components.component').then(
            (m) => m.DevComponentsComponent,
          ),
        title: 'Components',
      },
      {
        path: 'forbidden',
        loadComponent: () =>
          import('./features/forbidden/forbidden.component').then((m) => m.ForbiddenComponent),
        title: 'No access',
      },
      {
        path: 'not-found',
        loadComponent: () =>
          import('./features/not-found/not-found.component').then((m) => m.NotFoundComponent),
        title: 'Page Not Found',
      },
      {
        path: '**',
        loadComponent: () =>
          import('./features/not-found/not-found.component').then((m) => m.NotFoundComponent),
        title: 'Page Not Found',
      },
    ],
  },
];
